import { useTranslation, untranslated } from "@i18n";
import type { ComponentProps } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useState, useEffect } from "react";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import AttachIssues from "./AttachIssues/AttachIssues";
import AttachFiles from "./AttachFiles/AttachFiles";
import { showToastNotification } from "@Reducers/toastSlice";
import { createFileShortcut, consumeExternalFiles } from "@Actions/fileUpload";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import FileManager from "./FileManagerNew";
import { Box, CircularProgress } from "@mui/material";
import IconComponent from "@CubeComponents/icons";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import { FilesGrid } from "@Features/files";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import type { Space } from "@Components/WorkspaceList/useSelectSpaces";
import Text from "@CubeComponents/html/Text/Text";
import useFileManagerAsPopupShared from "./useFileManagerAsPopupShared";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { RootState } from "@Reducers/index";
import type { SelectedFileItem } from "@ServerTypes/folders/folders.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { TranslatedString } from "@i18n";

/**
 * A file row as the attach-issues flow carries it: the canonical item plus the version fields that
 * flow decides duplicates on. No index signature — `SelectedFileItem` is Zod-derived, and adding one
 * would stop plain `SelectedFileItem[]` from satisfying callbacks typed against this.
 */
type IssueFileRow = SelectedFileItem & {
  fileVersionNumber?: number;
  issueId?: ObjectIdString;
  issueVersionId?: ObjectIdString;
  status?: string;
};
interface FileManagerAsPopupProps {
  /**
   * METHOD syntax, not a property arrow, and that is deliberate: three unrelated callers supply this
   * (`Schedule/FileManagement`, `estimation/FilesTab`, and `FileManagerNew`'s merge/import handlers),
   * each declaring its own narrower payload shape. Method signatures are checked bivariantly even
   * under `strictFunctionTypes`, so all three remain assignable without an `any` or a cast at each
   * call site.
   */
  handleFileSelectedButtonClick?(selected: never, ...rest: never[]): unknown;
  handleFileSelectedCancelClick?: () => void;
  handleFolderSelected?(folder: never, ...rest: never[]): unknown;
  allowFolderSelectionCallback?: boolean; // default: false
  /** `null` at some call sites (mailbox), not just absent. */
  workspaceId?: string | null;
  id?: string | null;
  openThisSpaceByDefault?: boolean; // default: false
  hideIssues?: boolean; // default: false
  hideTasks?: boolean; // default: false
  hideReviews?: boolean; // default: false
  hideEstimates?: boolean; // default: false
  hideExternalSpace?: boolean; // default: false
  hideClashTests?: boolean; // default: false
  createShortcut?: boolean; // default: false
  consumeFiles?: boolean; // default: false
  hideSchedules?: boolean; // default: false
  hideContracts?: boolean; // default: false
  hideMergeFiles?: boolean; // default: false
  enableOnlyShareableResources?: boolean; // default: false
  /**
   * KNOWN BUG, preserved verbatim: the default below is `{}` — an OBJECT — while every use is
   * `shortCutData.map(...)`. An unset prop therefore throws rather than yielding an empty list.
   */
  shortCutData?: { fileVersionId?: ObjectIdString; fileId?: ObjectIdString; fileVersion?: string }[]; // default: {}
  hideAllTabs?: boolean; // default: false
  useAttachedExternalVolume?: boolean; // default: false
  heading?: string | null;
  allowInput?: boolean;
  inputLabel?: string;
  isTransmittal?: boolean;
  externalFolderData?: Record<string, unknown>;
  /** Callers map version ids straight off rows, so entries can be `undefined`. */
  disableFiles?: (string | undefined)[];
  minimumFiles?: number;
  errorMessage?: string;
  showOnlyMergedFile?: boolean;
  addEmptyContainerToList?: boolean;
  enableFilesTypes?: string[] | null;
  hideShortcuts?: boolean; // default: false
  createMerge?: boolean; // default: false
  attachSelectedBtnName?: string | null; // default: null
  /**
   * `unknown[]` at the `questionFields` call site: it passes `currentAnswer.value`, which the forms
   * answer type declares as `unknown`. Widened rather than asserted at that caller, since the value
   * genuinely is unvalidated there.
   */
  selectedFilesData?: SelectedFileItem[] | unknown; // default: []
  disableMultipleSelection?: boolean;
  projectId?: string | null;
  enableSpaceListDropdown?: boolean;
  setSpaceId?: (id: ObjectIdString) => void;
  disableContextMenu?: boolean; // default: false
  setIsLoadingCreateButton?: (loading: boolean) => void;
  isLoadingCreateButton?: boolean;
  isLoadingAddFiles?: boolean;
  setIsLoadingAddFiles?: (loading: boolean) => void;
  allowSwitchSpace?: boolean;
  hideFilesWithoutVersion?: boolean;
  allowViewToggle?: boolean;
  // Constrained list for the header Space dropdown. When provided, the dropdown shows only these
  // spaces (instead of fetching every project space) and switching is enabled if there is >1 option.
  spaceListDataSource?: Space[] | null;
}
function FileManagerAsPopup({
  handleFileSelectedButtonClick,
  handleFileSelectedCancelClick,
  handleFolderSelected,
  allowFolderSelectionCallback = false,
  workspaceId,
  openThisSpaceByDefault = false,
  hideIssues = false,
  hideTasks = false,
  hideReviews = false,
  hideEstimates = false,
  hideExternalSpace = false,
  hideClashTests = false,
  createShortcut = false,
  consumeFiles = false,
  hideSchedules = false,
  hideContracts = false,
  hideMergeFiles = false,
  enableOnlyShareableResources = false,
  shortCutData = {} as typeof shortCutData,
  hideAllTabs = true,
  useAttachedExternalVolume = false,
  heading,
  allowInput,
  inputLabel,
  isTransmittal,
  externalFolderData,
  disableFiles,
  minimumFiles,
  errorMessage,
  showOnlyMergedFile,
  addEmptyContainerToList,
  enableFilesTypes,
  hideShortcuts = false,
  createMerge = false,
  attachSelectedBtnName = null,
  selectedFilesData = [],
  disableMultipleSelection,
  projectId,
  setSpaceId,
  disableContextMenu = false,
  setIsLoadingCreateButton,
  isLoadingCreateButton,
  isLoadingAddFiles,
  setIsLoadingAddFiles,
  allowSwitchSpace = false,
  allowViewToggle = true,
  hideFilesWithoutVersion = false,
  spaceListDataSource,
}: Readonly<FileManagerAsPopupProps>) {
  const { t } = useTranslation();
  const { layout } = useSelector((state: RootState) => state);
  const CDEPLAN = layout.SPACES?.ENABLE;
  const PMPLAN = layout.FILES?.ENABLE;

  const dispatch = useDispatch();
  const fileManagerHeading = heading || "Attach Files";

  // Use shared hook for file selection logic (platform-agnostic)
  const {
    listOfSelectedFiles,
    setListOfSelectedFiles,
    selectedFolderData,
    inputValue,
    setInputValue,
    issuesHavingNoFiles,
    AddToListDisabled,
    disableAttachButton,
    disableCreateShortcutButton,
    handleFileSelectionCallback,
    handleAddToSelection,
    handleRemoveOfSelectedFile,
    handleFileSelectedButtonClickCallback,
    handleFolderSelectionCallback,
    handleFolderSelectedCallback,
    removeIssuesWithNoFiles,
    acceptFilesForIssues,
    // View mode state
    toggleFilesAsList,
    handleToggleFilesAsList,
    showViewToggle,
    // Space switching state
    switchSpaceId,
    showNoSpaceWarning,
    setShowNoSpaceWarning,
    handleSpaceListFetch,
    handleSpaceSelect,
  } = useFileManagerAsPopupShared({
    // These two props are declared with method syntax (bivariant) so three unrelated callers can
    // each supply their own payload shape; passing them on needs the concrete signature the hook
    // declares. Erased assertions.
    handleFileSelectedButtonClick: handleFileSelectedButtonClick as (files: SelectedFileItem[]) => void,
    handleFolderSelected: handleFolderSelected as (folder: SelectedFileItem) => void,
    minimumFiles,
    allowInput,
    disableMultipleSelection,
    isTransmittal,
    addEmptyContainerToList,
    createMerge,
    createShortcut,
    selectedFilesData: selectedFilesData as SelectedFileItem[],
    setIsLoadingCreateButton,
    setIsLoadingAddFiles,
    workspaceId: workspaceId as string,
    hasCDEPlan: !!CDEPLAN,
    hasPMPlan: !!PMPLAN,
    allowViewToggle,
  });

  // Web-specific state
  const [openAttachIssuesModal, setOpenAttachIssuesModal] = useState(false);
  const [filesIssues] = useState([]);
  // KNOWN BUG, preserved verbatim: initialised to `false`, but the duplicate-check flow later stores
  // an object here and reads `newState.payload` / `newState.response`. Until that first store, both
  // reads are `undefined` on a boolean. The union states both shapes instead of hiding one.
  const [newState, setNewState] = useState<
    boolean | { payload?: Record<string, unknown>; response?: { duplicatesFound?: number } }
  >(false);
  const [submitButtonLoading, setSubmitButtonLoading] = useState(false);

  // When a constrained space list is supplied, useSelectSpaces skips the project-spaces fetch, so the
  // fetch callback that normally clears the loading state never fires. Resolve the warning flag here
  // (mirrors handleSpaceListFetch: warn only when there is neither a workspace nor any space option).
  useEffect(() => {
    if (spaceListDataSource) {
      setShowNoSpaceWarning(!workspaceId && spaceListDataSource.length === 0);
    }
  }, [spaceListDataSource, workspaceId, setShowNoSpaceWarning]);

  const handleSetOpenAttachIssuesModal = (val: boolean) => {
    setOpenAttachIssuesModal(val);
  };

  const onAttachIssuesCallback = (data: {
    filesIssues?: IssueFileRow[];
    isSelected?: boolean;
    /** The selected issue statuses. `AttachIssues` holds this as a `string[]` (initialised `[]`). */
    selectedIssueType?: string[];
    selectedVersionType?: string;
  }) => {
    let filteredFilesIssues: IssueFileRow[] = [];
    const currentVersion = filesIssues.reduce(
      (acc: number, curr: IssueFileRow) => (acc > curr.fileVersionNumber! ? acc : curr.fileVersionNumber!),
      0,
    );

    if (data.selectedIssueType!.includes("open")) {
      filesIssues.forEach((file: IssueFileRow) => {
        if (file.status!.toLowerCase() == "open") {
          filteredFilesIssues.push(file);
        }
      });
    }
    if (data.selectedIssueType!.includes("in_review")) {
      filesIssues.forEach((file: IssueFileRow) => {
        if (file.status!.toLowerCase() == "in_review") {
          filteredFilesIssues.push(file);
        }
      });
    }
    if (data.selectedIssueType!.includes("closed")) {
      filesIssues.forEach((file: IssueFileRow) => {
        if (file.status!.toLowerCase() == "closed") {
          filteredFilesIssues.push(file);
        }
      });
    }
    if (data.selectedIssueType!.length == 0) {
      filteredFilesIssues = filesIssues;
    }

    if (data.selectedVersionType == "current_version") {
      filteredFilesIssues = filteredFilesIssues.filter(
        (file: IssueFileRow) => file.fileVersionNumber == currentVersion,
      );
    } else if (data.selectedVersionType == "previous_version") {
      filteredFilesIssues = filteredFilesIssues.filter(
        (file: IssueFileRow) => file.fileVersionNumber != currentVersion,
      );
    } else if (data.selectedVersionType == "") {
      filteredFilesIssues = [];
    }

    const selectedFilesToObj: Record<string, IssueFileRow> = { ...listOfSelectedFiles };
    filteredFilesIssues.map((file: IssueFileRow) => {
      selectedFilesToObj[file.issueVersionId!] = file;
    });
    setListOfSelectedFiles(selectedFilesToObj);
  };

  // handleCancelClickOnAttachFiles and handleAcceptClickOnAttachFiles are now in shared hook
  // as removeIssuesWithNoFiles and acceptFilesForIssues

  const handleCreateShortcut = async () => {
    setSubmitButtonLoading(true);
    const data = {
      ...shortCutData,
      spaceId: selectedFolderData.spaceId,
      parentId: selectedFolderData.hierarchyId,
      folderType: selectedFolderData.folderType,
    };
    const res = await createFileShortcut(data as Parameters<typeof createFileShortcut>[0]);
    handleFileSelectedCancelClick!();
    if (!res.data.status) {
      dispatch(
        showToastNotification({
          content: untranslated(res.data.errorMessage as string),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: t("Shortcut created successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    setSubmitButtonLoading(false);
  };

  const handleConsumeFiles = async (data?: Record<string, unknown>) => {
    setSubmitButtonLoading(true);
    if (!data) {
      data = {
        fileVersionIds: shortCutData.map((x: NonNullable<typeof shortCutData>[number]) => ({
          fileVersion: x.fileVersion,
          uploadNewVersion: false,
        })),
        spaceId: selectedFolderData.spaceId,
        parentId: selectedFolderData.hierarchyId,
        folderType: selectedFolderData.folderType,
        skipDuplicateCheck: false,
        transmittalData: externalFolderData,
      };
    }
    const res = await consumeExternalFiles(data as Parameters<typeof consumeExternalFiles>[0]);
    if (res.data.status) {
      if (res.data.duplicatesFound) {
        dispatch(
          showToastNotification({
            content: t("Duplicate file(s) found so files were not consumed."),
            type: TOAST_TYPE_ERROR,
          }),
        );
        setNewState({
          response: res.data as { result?: unknown } as { duplicatesFound?: number },
          payload: data,
        });
      } else {
        dispatch(
          showToastNotification({
            content: t("File(s) consumed successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      }
    } else if (!res.data.status) {
      if (res.data.errorCode) {
        dispatch(
          showToastNotification({
            content: t("Some Error(s) occured. Please check Escrow files section."),
            type: TOAST_TYPE_WARNING,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(res.data.errorMessage as string),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    }
    setSubmitButtonLoading(false);
    handleFileSelectedCancelClick!();
  };

  const handleDuplicateClick = async (button: string) => {
    const newPayload = {
      ...(newState as { payload?: Record<string, unknown> }).payload,
      skipDuplicateCheck: true,
    };
    // `newPayload` spreads `newState.payload`, whose members are `unknown`. Both assertions are
    // inline and erased — introducing a local would have added an emitted declaration.
    (newPayload as typeof newPayload & { fileVersionIds: { fileVersionId?: ObjectIdString }[] }).fileVersionIds = (
      newPayload as typeof newPayload & { fileVersionIds: { fileVersionId?: ObjectIdString }[] }
    ).fileVersionIds.map((x) => ({
      ...x,
      [button]: true,
    }));
    setNewState(false);
    await handleConsumeFiles(newPayload);
  };

  function footer() {
    return (
      <div className="flex flex-row gap-6 py-4 px-6 justify-end">
        <ButtonComponent
          onClick={() => handleDuplicateClick("uploadNewVersion")}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        >
          <Text>{t("Upload all as new versions")}</Text>
        </ButtonComponent>

        <ButtonComponent
          onClick={() => handleDuplicateClick("uploadNewFile")}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        >
          <Text>{t("Upload all as new files")}</Text>
        </ButtonComponent>
      </div>
    );
  }

  function modelContent() {
    return (
      <div className="my-5">{`${(newState as { response?: { duplicatesFound?: number } }).response!.duplicatesFound} files have duplicates at the selected location. How do you want to proceed?`}</div>
    );
  }

  function popUpContent() {
    return (
      <div className="flex flex-col p-6 gap-6 overflow-auto h-full">
        <div className="flex flex-col gap-6 p-4 border border-button_light rounded-lg">
          {!showNoSpaceWarning && (
            <>
              {(showViewToggle || allowInput) && (
                <div className="flex flex-row gap-4 items-center mx-1">
                  {allowInput && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center">
                        <span>{inputLabel}</span>
                        <div className="ps-4">
                          <TextFieldComponent
                            type="text"
                            name="mergedFileName"
                            placeholder={t("Add name here")}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            slotProps={{
                              htmlInput: {
                                "data-testid": "addMergeFileName",
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {showViewToggle && (
                    <div className="flex items-center gap-2">
                      <ToggleSwitchComponent checked={!toggleFilesAsList} onChange={handleToggleFilesAsList} />
                      {toggleFilesAsList ? (
                        <Text className="text-sm">{t("Seeing files as a list")}</Text>
                      ) : (
                        <Text className="text-sm">{t("Seeing files as folder structure")}</Text>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {getContent()}
          {!showNoSpaceWarning && (
            <>
              {!createShortcut && (
                <div className="col-start-2 row-start-1">
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={handleAddToSelection}
                    disabled={AddToListDisabled}
                    data-testid="addFilesToSelectionBelowBtn"
                  >
                    <div className="flex flex-row gap-2 items-center">
                      <Text>{t("Add to Selection Below")}</Text>
                    </div>
                  </ButtonComponent>
                </div>
              )}
            </>
          )}
        </div>
        {!showNoSpaceWarning && (
          <div className="flex justify-between overflow-auto max-h-[15.625rem] flex-shrink-0">
            {!createShortcut && (
              <div className="flex flex-col w-full border border-button_light rounded-lg">
                <Text className="flex p-4  w-full border-b border-button_light font-semibold text-base">
                  {t("Selection")}
                </Text>
                <div className="flex flex-col gap-4 p-4 w-full">
                  <div>
                    <ButtonComponent
                      buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                      disabled={Object.keys(listOfSelectedFiles).length === 0}
                      onClick={() => setListOfSelectedFiles({})}
                    >
                      <Text>{t("Remove All")}</Text>
                    </ButtonComponent>
                  </div>
                  <div className="flex gap-5 flex-row overflow-auto">
                    {Object.keys(listOfSelectedFiles).length === 0 ? (
                      <Text className="text-sm leading-[120% font-semibold text-yet_to_start_communication py-2">
                        {t("No File(s) Added")}
                      </Text>
                    ) : (
                      Object.keys(listOfSelectedFiles).map((file) => {
                        if (
                          [
                            "RESOURCE_TYPE_FILE",
                            "RESOURCE_TYPE_MERGED_FILE",
                            "RESOURCE_TYPE_ESTIMATE",
                            "RESOURCE_TYPE_EMBED",
                          ].includes(listOfSelectedFiles[file]!.resourceType!) ||
                          Object.hasOwn(listOfSelectedFiles[file]!, "dev")
                        ) {
                          return (
                            <div
                              key={file}
                              className="flex border border-stroke_primary gap-2 p-1.5 rounded items-center flex-shrink-0"
                            >
                              <div className="w-4 h-4 flex items-center justify-center">
                                {getFileIcon(listOfSelectedFiles[file]!.name || listOfSelectedFiles[file]!.fileName)}
                              </div>
                              <div className="text-sm leading-[120%]">
                                {listOfSelectedFiles[file]!.name || listOfSelectedFiles[file]!.fileName}
                              </div>
                              <IconComponent
                                name="Cancel"
                                onClick={() => handleRemoveOfSelectedFile(listOfSelectedFiles[file]!)}
                                width="1rem"
                                height="1rem"
                              />
                            </div>
                          );
                        }
                        return null;
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {errorMessage && (
          <div className="ps-4 pt-4 text-[var(--negative-communication)]">
            <span>{errorMessage}</span>
          </div>
        )}
      </div>
    );
  }

  function popUpFooter() {
    return (
      <div className="flex flex-row gap-6 py-4 px-6">
        {!createShortcut && !showNoSpaceWarning && (
          <ButtonComponent
            buttonType="gradientButton"
            onClick={handleFolderSelected ? handleFolderSelectedCallback : handleFileSelectedButtonClickCallback}
            disabled={disableAttachButton}
            loading={isLoadingCreateButton || isLoadingAddFiles}
            data-testid="attachSelectedBtn"
          >
            {attachSelectedBtnName || t("Attach")}
          </ButtonComponent>
        )}
        {createShortcut && !consumeFiles && (
          <ButtonComponent
            buttonType="gradientButton"
            onClick={handleCreateShortcut}
            disabled={disableCreateShortcutButton}
          >
            <Text>{t("Create here")}</Text>
          </ButtonComponent>
        )}
        {consumeFiles && (
          <ButtonComponent
            buttonType="gradientButton"
            onClick={() => handleConsumeFiles()}
            loading={submitButtonLoading}
            disabled={disableCreateShortcutButton}
          >
            <Text>{t("Consume here")}</Text>
          </ButtonComponent>
        )}
        <ButtonComponent onClick={handleFileSelectedCancelClick} data-testid="cancelSelectedFileBtn">
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );
  }

  const getContent = () => {
    if (showNoSpaceWarning == null) {
      return (
        <div className="flex justify-center h-full flex-col gap-2 items-center">
          <CircularProgress />
        </div>
      );
    } else if (showNoSpaceWarning) {
      return (
        <Text className="flex justify-center h-full flex-col gap-2 items-center">
          <IconComponent name="InspectionFormNoData" width="18.75rem" height="18.75rem" />
          {t("No Spaces available in this project")}
        </Text>
      );
    } else if (!showNoSpaceWarning) {
      return (
        <div className={`flex border border-button_light rounded-lg h-[27.75rem] overflow-auto`}>
          {toggleFilesAsList ? (
            <FilesGrid
              initialShowAllVersions={false}
              spaceId={allowSwitchSpace ? null : switchSpaceId}
              projectId={projectId!}
              showTitle={false}
              showToolbar={{ views: true, group: false, upload: false, action: false, pinnedColumns: false }}
              propertiesPanel={false}
              disableMenuOptions
              excelExport={false}
              // `FilesGrid` declares its own flattened selection payload; referenced through the
              // component's props rather than exporting that type. Single erased assertion.
              handleFileSelectionCallback={
                handleFileSelectionCallback as ComponentProps<typeof FilesGrid>["handleFileSelectionCallback"]
              }
              showOnlyMergedFile={showOnlyMergedFile}
              disableFiles={disableFiles as string[]}
            />
          ) : (
            <FileManager
              projectId={projectId!}
              spaceId={switchSpaceId || ""}
              fileManagerAsPopup
              openThisSpaceByDefault={openThisSpaceByDefault}
              handleFileSelectionCallback={handleFileSelectionCallback as (files: SelectedFileItem[]) => void}
              handleFolderSelectionCallback={handleFolderSelectionCallback}
              allowFolderSelectionCallback={allowFolderSelectionCallback}
              hideIssues={hideIssues}
              hideTasks={hideTasks}
              hideReviews={hideReviews}
              hideAllTabs={hideAllTabs}
              hideEstimates={hideEstimates}
              hideSchedules={hideSchedules}
              hideContracts={hideContracts}
              hideMergeFiles={hideMergeFiles}
              hideExternalSpace={hideExternalSpace}
              useAttachedExternalVolume={useAttachedExternalVolume}
              hideClashTests={hideClashTests}
              // enablePersistence={false}
              createShortcut={createShortcut}
              disableFiles={disableFiles}
              enableFilesTypes={enableFilesTypes}
              hideShortcuts={hideShortcuts}
              hideUpload
              showOnlyMergedFile={showOnlyMergedFile}
              alreadySelected={selectedFilesData as SelectedFileItem[]}
              setSpaceId={setSpaceId}
              disableContextMenu={disableContextMenu}
              enableOnlyShareableResources={enableOnlyShareableResources}
              disableDragAndDropUpload={true}
              hideFilesWithoutVersion={hideFilesWithoutVersion}
            />
          )}
        </div>
      );
    } else return "";
  };
  return (
    <>
      <div>
        {openAttachIssuesModal && (
          <Box
            onClick={(e: { stopPropagation: () => void }) => {
              e.stopPropagation();
            }}
          >
            <AttachIssues
              handleSetOpenAttachIssuesModal={handleSetOpenAttachIssuesModal}
              onSuccess={onAttachIssuesCallback}
            />
          </Box>
        )}
        {issuesHavingNoFiles.length > 0 && (
          <Box
            onClick={(e: { stopPropagation: () => void }) => {
              e.stopPropagation();
            }}
          >
            <AttachFiles onCancelClick={removeIssuesWithNoFiles} onAcceptClick={acceptFilesForIssues} />
          </Box>
        )}
        <ModalStateDialogComponent
          modalPadding={0}
          headerTitle={
            <div className="flex flex-row ps-6 py-4 w-full justify-between items-center">
              <ToolTipComponent content={fileManagerHeading as TranslatedString}>
                <span className="text-2xl font-semibold truncate max-w-[50%]" data-testid="fileManagerHeading">
                  {fileManagerHeading}
                </span>
              </ToolTipComponent>
              {!showNoSpaceWarning && !(showViewToggle && toggleFilesAsList) && (
                <SelectSpaces
                  disablePermissionCheck
                  projectId={projectId!}
                  workspaceId={switchSpaceId}
                  dataSource={spaceListDataSource}
                  isDisabled={
                    toggleFilesAsList ||
                    (PMPLAN && !CDEPLAN) ||
                    (!allowSwitchSpace && (spaceListDataSource?.length ?? 0) <= 1)
                  }
                  selectedSpaceCallback={handleSpaceSelect}
                  fetchSpaceListCallback={handleSpaceListFetch}
                />
              )}
            </div>
          }
          footerContent={popUpFooter()}
          modelContent={popUpContent()}
          isVisible
          width="90%"
          height="90%"
          onCancel={handleFileSelectedCancelClick}
        />
      </div>
      {newState && (
        <ModalStateDialogComponent
          headerTitle={t("Duplicates found")}
          footerContent={footer()}
          modelContent={modelContent()}
          isVisible={newState as boolean}
          width="40%"
          onCancel={() => setNewState(false)}
        />
      )}
    </>
  );
}

export default FileManagerAsPopup;
